import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import QuestionPickerModal from "./QuestionPickerModal";
import {
  getTestById,
  createTest,
  updateTest,
  getQuestionsList,
} from "../../../../services/adminService";
import { useAdminAuth } from "../../../../contexts/AdminAuthContext";
import { ROUTES } from "../../../../constants/routes";
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Save,
  Send,
  EyeOff,
  CheckCircle2,
  Layers,
  HelpCircle,
} from "lucide-react";

export default function TestBuilderPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { adminUser } = useAdminAuth();

  // Check if editing an existing test passed via state or query
  const queryParams = new URLSearchParams(location.search);
  const testIdParam = queryParams.get("testId") || location.state?.testId;

  const [allQuestionsMap, setAllQuestionsMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Test form state
  const [testForm, setTestForm] = useState({
    id: null,
    title: "",
    code: "",
    description: "",
    duration_minutes: 60,
    total_marks: 50,
    negative_marking: 0.66,
    instructions: "1. All questions are compulsory.\n2. Each question carries marks as indicated.\n3. Negative marking is applicable for incorrect answers.",
    sections: [
      {
        id: `SEC-${Date.now()}`,
        title: "Section A - General Studies",
        description: "Core Prelims syllabus topics",
        question_ids: [],
      },
    ],
    status: "Draft",
    plan_tier: "All",
    attempt_limit: 1,
    availability_start: new Date().toISOString().split("T")[0] + "T00:00",
    availability_end: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split("T")[0] + "T23:59",
  });

  const [activeSectionIdForPicker, setActiveSectionIdForPicker] = useState(null);

  // Load question map to display full question text and metadata
  useEffect(() => {
    getQuestionsList().then((data) => {
      const map = {};
      data.forEach((q) => {
        map[q.id] = q;
      });
      setAllQuestionsMap(map);
    });
  }, []);

  // Load existing test if editing
  useEffect(() => {
    if (testIdParam) {
      setLoading(true);
      getTestById(testIdParam)
        .then((test) => {
          setTestForm({
            ...test,
            availability_start: test.availability_start ? test.availability_start.substring(0, 16) : "",
            availability_end: test.availability_end ? test.availability_end.substring(0, 16) : "",
          });
        })
        .catch((err) => {
          console.error("Failed to load test:", err);
        })
        .finally(() => setLoading(false));
    }
  }, [testIdParam]);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(""), 3500);
  };

  // Section manipulation
  const handleAddSection = () => {
    const newSec = {
      id: `SEC-${Date.now()}`,
      title: `Section ${String.fromCharCode(65 + testForm.sections.length)}`,
      description: "",
      question_ids: [],
    };
    setTestForm({ ...testForm, sections: [...testForm.sections, newSec] });
  };

  const handleRemoveSection = (sectionId) => {
    if (testForm.sections.length <= 1) {
      alert("A test must have at least one section.");
      return;
    }
    setTestForm({
      ...testForm,
      sections: testForm.sections.filter((s) => s.id !== sectionId),
    });
  };

  const handleUpdateSectionTitle = (sectionId, title) => {
    setTestForm({
      ...testForm,
      sections: testForm.sections.map((s) =>
        s.id === sectionId ? { ...s, title } : s
      ),
    });
  };

  const handleAddQuestionsToSection = (sectionId, questionIds) => {
    setTestForm({
      ...testForm,
      sections: testForm.sections.map((s) =>
        s.id === sectionId
          ? { ...s, question_ids: [...new Set([...s.question_ids, ...questionIds])] }
          : s
      ),
    });
  };

  const handleRemoveQuestionFromSection = (sectionId, qId) => {
    setTestForm({
      ...testForm,
      sections: testForm.sections.map((s) =>
        s.id === sectionId
          ? { ...s, question_ids: s.question_ids.filter((id) => id !== qId) }
          : s
      ),
    });
  };

  const handleReorderQuestion = (sectionId, qIndex, direction) => {
    setTestForm({
      ...testForm,
      sections: testForm.sections.map((sec) => {
        if (sec.id !== sectionId) return sec;
        const newQIds = [...sec.question_ids];
        const targetIndex = direction === "up" ? qIndex - 1 : qIndex + 1;
        if (targetIndex < 0 || targetIndex >= newQIds.length) return sec;
        const temp = newQIds[qIndex];
        newQIds[qIndex] = newQIds[targetIndex];
        newQIds[targetIndex] = temp;
        return { ...sec, question_ids: newQIds };
      }),
    });
  };

  // Compute total questions
  const totalQuestionsCount = testForm.sections.reduce(
    (acc, sec) => acc + sec.question_ids.length,
    0
  );

  const handleSave = async (targetStatus) => {
    if (!testForm.title.trim()) {
      alert("Please provide a Test Title.");
      return;
    }
    if (totalQuestionsCount === 0) {
      alert("Please add at least one question from the Question Bank.");
      return;
    }

    try {
      const payload = {
        ...testForm,
        status: targetStatus || testForm.status,
        is_active: (targetStatus || testForm.status) === "Published",
      };

      if (testForm.id) {
        await updateTest(testForm.id, payload, adminUser.email);
        showToast(`Test ${testForm.id} updated successfully!`);
      } else {
        const created = await createTest(payload, adminUser.email);
        showToast(`Test ${created.id} created successfully!`);
        setTestForm({ ...testForm, id: created.id });
      }

      // Navigate to Test Management after brief delay
      setTimeout(() => {
        navigate(ROUTES.ADMIN_TESTS_MANAGEMENT);
      }, 1000);
    } catch (err) {
      console.error(err);
      alert("Failed to save test: " + err.message);
    }
  };

  return (
    <div>
      {/* Toast Message */}
      {toastMessage && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            background: "#0f172a",
            color: "#ffffff",
            padding: "12px 20px",
            borderRadius: "8px",
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            zIndex: 2000,
            fontSize: "14px",
          }}
        >
          <CheckCircle2 size={18} style={{ color: "#22c55e" }} />
          {toastMessage}
        </div>
      )}

      {/* Header with Title and Action Buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div>
          <h2 style={{ fontSize: "18px", fontWeight: 700, margin: "0 0 4px 0", color: "#1e293b" }}>
            {testForm.id ? `Edit Test: ${testForm.id}` : "Create New UPSC Test"}
          </h2>
          <span style={{ fontSize: "13px", color: "#64748b" }}>
            Status: <span className={`status-badge ${(testForm.status || "draft").toLowerCase()}`}>{testForm.status}</span>
            &bull; Total Questions: <strong>{totalQuestionsCount}</strong>
          </span>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            className="admin-btn admin-btn-secondary"
            onClick={() => handleSave("Draft")}
          >
            <Save size={15} /> Save as Draft
          </button>
          {testForm.status === "Published" ? (
            <button
              className="admin-btn admin-btn-secondary"
              onClick={() => handleSave("Draft")}
            >
              <EyeOff size={15} /> Unpublish
            </button>
          ) : (
            <button
              className="admin-btn admin-btn-primary"
              onClick={() => handleSave("Published")}
            >
              <Send size={15} /> Publish Test
            </button>
          )}
        </div>
      </div>

      {/* Grid: Left Column (Metadata), Right Column (Sections & Questions) */}
      <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: "24px", alignItems: "flex-start" }}>
        {/* Left Column: Metadata & Rules */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Test Configuration</h3>
          </div>
          <div className="admin-card-body">
            <div className="admin-form-group">
              <label className="admin-form-label">Test Title *</label>
              <input
                type="text"
                className="admin-form-input"
                placeholder="e.g. UPSC Prelims 2024 - GS Mock 1"
                value={testForm.title}
                onChange={(e) => setTestForm({ ...testForm, title: e.target.value })}
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Test Code / Identifier</label>
              <input
                type="text"
                className="admin-form-input"
                placeholder="e.g. UPSC-PRE-2024-M01"
                value={testForm.code}
                onChange={(e) => setTestForm({ ...testForm, code: e.target.value })}
              />
            </div>

            <div className="admin-form-row">
              <div className="admin-form-group">
                <label className="admin-form-label">Duration (Mins)</label>
                <input
                  type="number"
                  className="admin-form-input"
                  value={testForm.duration_minutes}
                  onChange={(e) =>
                    setTestForm({ ...testForm, duration_minutes: parseInt(e.target.value) || 60 })
                  }
                />
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Total Marks</label>
                <input
                  type="number"
                  className="admin-form-input"
                  value={testForm.total_marks}
                  onChange={(e) =>
                    setTestForm({ ...testForm, total_marks: parseInt(e.target.value) || 50 })
                  }
                />
              </div>
            </div>

            <div className="admin-form-row">
              <div className="admin-form-group">
                <label className="admin-form-label">Negative Marking</label>
                <input
                  type="number"
                  step="0.01"
                  className="admin-form-input"
                  value={testForm.negative_marking}
                  onChange={(e) =>
                    setTestForm({ ...testForm, negative_marking: parseFloat(e.target.value) || 0.66 })
                  }
                />
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Attempt Limit</label>
                <input
                  type="number"
                  className="admin-form-input"
                  value={testForm.attempt_limit}
                  onChange={(e) =>
                    setTestForm({ ...testForm, attempt_limit: parseInt(e.target.value) || 1 })
                  }
                />
              </div>
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Plan / Entitlement Tier</label>
              <select
                className="admin-form-select"
                value={testForm.plan_tier}
                onChange={(e) => setTestForm({ ...testForm, plan_tier: e.target.value })}
              >
                <option value="All">All Users (Free & Paid)</option>
                <option value="Free">Free Tier Only</option>
                <option value="Premium">Premium Subscribers Only</option>
              </select>
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Availability Window</label>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <div>
                  <span style={{ fontSize: "11px", color: "#64748b" }}>Start:</span>
                  <input
                    type="datetime-local"
                    className="admin-form-input"
                    value={testForm.availability_start}
                    onChange={(e) => setTestForm({ ...testForm, availability_start: e.target.value })}
                  />
                </div>
                <div>
                  <span style={{ fontSize: "11px", color: "#64748b" }}>End:</span>
                  <input
                    type="datetime-local"
                    className="admin-form-input"
                    value={testForm.availability_end}
                    onChange={(e) => setTestForm({ ...testForm, availability_end: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">General Instructions</label>
              <textarea
                className="admin-form-textarea"
                rows={3}
                value={testForm.instructions}
                onChange={(e) => setTestForm({ ...testForm, instructions: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Right Column: Sections & Questions Picker */}
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <h3 style={{ fontSize: "16px", fontWeight: 700, margin: 0, color: "#1e293b" }}>
              Test Sections & Question Bank Mapping
            </h3>
            <button
              className="admin-btn admin-btn-secondary admin-btn-sm"
              onClick={handleAddSection}
            >
              <Plus size={14} /> Add Section
            </button>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {testForm.sections.map((section, sIndex) => (
              <div key={section.id} className="admin-card" style={{ marginBottom: 0 }}>
                <div
                  className="admin-card-header"
                  style={{ background: "#f8fafc", padding: "12px 18px" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                    <Layers size={16} style={{ color: "#0284c7" }} />
                    <input
                      type="text"
                      className="admin-form-input"
                      style={{ maxWidth: "320px", fontWeight: 600, padding: "5px 8px" }}
                      value={section.title}
                      onChange={(e) => handleUpdateSectionTitle(section.id, e.target.value)}
                    />
                    <span style={{ fontSize: "12px", color: "#64748b" }}>
                      ({section.question_ids.length} questions)
                    </span>
                  </div>

                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      className="admin-btn admin-btn-primary admin-btn-sm"
                      onClick={() => setActiveSectionIdForPicker(section.id)}
                    >
                      <Plus size={13} /> Add from Question Bank
                    </button>
                    {testForm.sections.length > 1 && (
                      <button
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        style={{ color: "#dc2626" }}
                        onClick={() => handleRemoveSection(section.id)}
                        title="Remove section"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="admin-card-body" style={{ padding: "16px" }}>
                  {section.question_ids.length === 0 ? (
                    <div
                      style={{
                        padding: "24px",
                        textAlign: "center",
                        border: "1px dashed var(--admin-border)",
                        borderRadius: "8px",
                        color: "#64748b",
                        fontSize: "13px",
                      }}
                    >
                      No questions in this section yet. Click "Add from Question Bank" to select questions.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {section.question_ids.map((qId, qIndex) => {
                        const question = allQuestionsMap[qId];
                        return (
                          <div
                            key={qId}
                            style={{
                              border: "1px solid var(--admin-border)",
                              borderRadius: "6px",
                              padding: "10px 14px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              background: "#ffffff",
                              gap: "12px",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <span
                                style={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  color: "#64748b",
                                  width: "24px",
                                }}
                              >
                                #{qIndex + 1}
                              </span>
                              <span style={{ fontWeight: 700, fontSize: "12px", color: "#0284c7" }}>
                                {qId}
                              </span>
                              <div style={{ maxWidth: "460px" }}>
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: "13px",
                                    color: "#1e293b",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  title={question?.question || ""}
                                >
                                  {question ? question.question : `Question ${qId}`}
                                </p>
                                {question && (
                                  <span style={{ fontSize: "11px", color: "#64748b" }}>
                                    {question.subject} &bull; {question.topic} &bull; Ans: Option {question.correct_answer}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <button
                                className="admin-btn admin-btn-secondary admin-btn-sm"
                                disabled={qIndex === 0}
                                onClick={() => handleReorderQuestion(section.id, qIndex, "up")}
                                title="Move question up"
                              >
                                <ArrowUp size={12} />
                              </button>
                              <button
                                className="admin-btn admin-btn-secondary admin-btn-sm"
                                disabled={qIndex === section.question_ids.length - 1}
                                onClick={() => handleReorderQuestion(section.id, qIndex, "down")}
                                title="Move question down"
                              >
                                <ArrowDown size={12} />
                              </button>
                              <button
                                className="admin-btn admin-btn-secondary admin-btn-sm"
                                style={{ color: "#ef4444" }}
                                onClick={() => handleRemoveQuestionFromSection(section.id, qId)}
                                title="Remove from section"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Central Question Bank Picker Modal */}
      <QuestionPickerModal
        isOpen={Boolean(activeSectionIdForPicker)}
        onClose={() => setActiveSectionIdForPicker(null)}
        alreadySelectedIds={
          activeSectionIdForPicker
            ? testForm.sections.find((s) => s.id === activeSectionIdForPicker)?.question_ids || []
            : []
        }
        onAddQuestions={(selectedIds) => {
          if (activeSectionIdForPicker) {
            handleAddQuestionsToSection(activeSectionIdForPicker, selectedIds);
          }
        }}
      />
    </div>
  );
}
